import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_TQL_TRAX_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableTQLTraxAppInput = {
  appType: AppTypeEnum
  clientID: string
  ocpApimSubscriptionKey: string
  password: string
  username: string
}

const formSchema = z.object({
  clientID: z.string().min(1, 'Client ID is required'),
  ocpApimSubscriptionKey: z.string().min(1, 'Ocp Apim Subscription Key is required'),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the TQL Trax app.
 */
export function TQLTraxModal({ isOpen, onClose, appType }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      clientID: '',
      ocpApimSubscriptionKey: '',
      password: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableTQLTraxApp, { loading }] = useMutation<
    { enableTQLTraxApp: { clientID: string; ocpApimSubscriptionKey: string; password: string; username: string } },
    EnableTQLTraxAppInput
  >(ENABLE_TQL_TRAX_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('TQL Trax app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Incorrect API key'
          ? 'Invalid API key or username/password. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableTQLTraxApp({
      variables: {
        appType: appType as AppTypeEnum,
        clientID: values.clientID,
        ocpApimSubscriptionKey: values.ocpApimSubscriptionKey,
        password: values.password,
        username: values.username
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable TQL Trax for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires an admin TQL Trax account. Enter your TQL Trax Client ID, Username,
            Password and Ocp Apim Subscription Key.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="clientID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TQL Trax Client ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your TQL Trax Client ID" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TQL Trax Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your TQL Trax Username" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TQL Trax Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your TQL Trax Password" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ocpApimSubscriptionKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TQL Trax Ocp Apim Subscription Key</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your TQL Trax Ocp Apim Subscription Key" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/tql-trax"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
